import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface IConfigGeneralEntityAttributes extends IModelAttributes {
	generalTerms: string;
	vehicleTerms: string;
	passengerTerms: string;
	freightTerms: string;
	defaultEventTerms: string;
	defaultParkingTerms: string;
	defaultVoucherTerms: string;
	defaultProductTerms: string;
	companyName: string;
	abn: string;
	addressLine1: string;
	addressLine2: string;
	gstPercentage: number;
	creditCardSurchargePercentage: number;
	reportTimezone: string;
	passengersDefaultToReturn: boolean;
	vehicleDefaultToReturn: boolean;
	defaultBookingType: Enums.defaultBookingType;
	timeFormat: Enums.timeFormat;
	systemOfMeasurement: Enums.systemOfMeasurementType;
	dateFormat: string;
	defaultCurrencyCode: string;
	defaultNumberFormattingCode: string;
	businessContactNumber: string;

	defaultLanguageId?: string;
	defaultLanguage?: Models.LanguageEntity | Models.ILanguageEntityAttributes;
	defaultRouteId?: string;
	defaultRoute?: Models.RouteEntity | Models.IRouteEntityAttributes;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	defaultPricingStrategyProfileId?: string;
	defaultPricingStrategyProfile?:
		| Models.PricingStrategyProfileEntity
		| Models.IPricingStrategyProfileEntityAttributes;
	defaultVehicleBaseMeasurementId?: string;
	defaultVehicleBaseMeasurement?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
}
@entity('ConfigGeneralEntity', 'Config General')
export default class ConfigGeneralEntity extends Model implements IConfigGeneralEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * This is the general terms and conditions
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public generalTerms: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public vehicleTerms: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public passengerTerms: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public freightTerms: string;

	/**
	 * The default event terms and conditions to pre-fill on a new event.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultEventTerms: string;

	/**
	 * Default parking terms and conditions. Used as fallback when a ParkingLocation has no specific terms.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultParkingTerms: string;

	/**
	 * Default voucher/gift certificate terms and conditions. Shown during voucher purchase.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultVoucherTerms: string;

	/**
	 * Default product terms and conditions for entitlement products.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultProductTerms: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public companyName: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public abn: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public addressLine1: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public addressLine2: string;

	/**
	 * The GST that is applied throughout the application.
	 */
	@observable
	@attribute<ConfigGeneralEntity, number>()
	public gstPercentage: number;

	@observable
	@attribute<ConfigGeneralEntity, number>()
	public creditCardSurchargePercentage: number;

	/**
	 * Timezone used to evaluate dates in reports.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public reportTimezone: string;

	/**
	 * During the booking wizard whether the passenger booking defaults to return or one-way.
	 */
	@observable
	@attribute<ConfigGeneralEntity, boolean>()
	public passengersDefaultToReturn: boolean = false;

	/**
	 * During the booking wizard whether the vehicle booking defaults to return or one-way.
	 */
	@observable
	@attribute<ConfigGeneralEntity, boolean>()
	public vehicleDefaultToReturn: boolean = false;

	/**
	 * The booking type that will default throughout the application, e.g. passenger or vehicle.
	 */
	@observable
	@attribute<ConfigGeneralEntity, Enums.defaultBookingType>()
	public defaultBookingType: Enums.defaultBookingType;

	/**
	 * The time format that will be used throughout the application.
	 */
	@observable
	@attribute<ConfigGeneralEntity, Enums.timeFormat>()
	public timeFormat: Enums.timeFormat;

	/**
	 * The measurement system used by the application.
	 */
	@observable
	@attribute<ConfigGeneralEntity, Enums.systemOfMeasurementType>()
	public systemOfMeasurement: Enums.systemOfMeasurementType;

	/**
	 * The format that will be used by dates in the application.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public dateFormat: string;

	/**
	 * The default currency used on the website.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultCurrencyCode: string;

	/**
	 * The code for the default number formatting in the application.
	 */
	@observable
	@attribute<ConfigGeneralEntity, string>()
	public defaultNumberFormattingCode: string;

	@observable
	@attribute<ConfigGeneralEntity, string>()
	public businessContactNumber: string;

	@observable
	@attribute()
	public defaultLanguageId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultLanguage: Models.LanguageEntity;

	@observable
	@attribute()
	public defaultRouteId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultRoute: Models.RouteEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation?: Models.OrganisationEntity;

	@observable
	@attribute()
	public defaultPricingStrategyProfileId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultPricingStrategyProfile: Models.PricingStrategyProfileEntity;

	@observable
	@attribute()
	public defaultVehicleBaseMeasurementId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultVehicleBaseMeasurement: Models.MeasurementEntity;

	constructor(attributes?: Partial<IConfigGeneralEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IConfigGeneralEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.generalTerms !== undefined) {
				this.generalTerms = attributes.generalTerms;
			}
			if (attributes.vehicleTerms !== undefined) {
				this.vehicleTerms = attributes.vehicleTerms;
			}
			if (attributes.passengerTerms !== undefined) {
				this.passengerTerms = attributes.passengerTerms;
			}
			if (attributes.freightTerms !== undefined) {
				this.freightTerms = attributes.freightTerms;
			}
			if (attributes.defaultEventTerms !== undefined) {
				this.defaultEventTerms = attributes.defaultEventTerms;
			}
			if (attributes.defaultParkingTerms !== undefined) {
				this.defaultParkingTerms = attributes.defaultParkingTerms;
			}
			if (attributes.defaultVoucherTerms !== undefined) {
				this.defaultVoucherTerms = attributes.defaultVoucherTerms;
			}
			if (attributes.defaultProductTerms !== undefined) {
				this.defaultProductTerms = attributes.defaultProductTerms;
			}
			if (attributes.companyName !== undefined) {
				this.companyName = attributes.companyName;
			}
			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}
			if (attributes.addressLine1 !== undefined) {
				this.addressLine1 = attributes.addressLine1;
			}
			if (attributes.addressLine2 !== undefined) {
				this.addressLine2 = attributes.addressLine2;
			}
			if (attributes.gstPercentage !== undefined) {
				this.gstPercentage = attributes.gstPercentage;
			}
			if (attributes.creditCardSurchargePercentage !== undefined) {
				this.creditCardSurchargePercentage = attributes.creditCardSurchargePercentage;
			}
			if (attributes.reportTimezone !== undefined) {
				this.reportTimezone = attributes.reportTimezone;
			}
			if (attributes.passengersDefaultToReturn !== undefined) {
				this.passengersDefaultToReturn = attributes.passengersDefaultToReturn;
			}
			if (attributes.vehicleDefaultToReturn !== undefined) {
				this.vehicleDefaultToReturn = attributes.vehicleDefaultToReturn;
			}
			if (attributes.defaultBookingType !== undefined) {
				this.defaultBookingType = attributes.defaultBookingType;
			}
			if (attributes.timeFormat !== undefined) {
				this.timeFormat = attributes.timeFormat;
			}
			if (attributes.systemOfMeasurement !== undefined) {
				this.systemOfMeasurement = attributes.systemOfMeasurement;
			}
			if (attributes.dateFormat !== undefined) {
				this.dateFormat = attributes.dateFormat;
			}
			if (attributes.defaultCurrencyCode !== undefined) {
				this.defaultCurrencyCode = attributes.defaultCurrencyCode;
			}
			if (attributes.defaultNumberFormattingCode !== undefined) {
				this.defaultNumberFormattingCode = attributes.defaultNumberFormattingCode;
			}
			if (attributes.businessContactNumber !== undefined) {
				this.businessContactNumber = attributes.businessContactNumber;
			}
			if (attributes.defaultLanguageId !== undefined) {
				this.defaultLanguageId = attributes.defaultLanguageId;
			}
			if (attributes.defaultLanguage !== undefined) {
				if (attributes.defaultLanguage === null) {
					this.defaultLanguage = attributes.defaultLanguage;
				} else if (attributes.defaultLanguage instanceof Models.LanguageEntity) {
					this.defaultLanguage = attributes.defaultLanguage;
					this.defaultLanguageId = attributes.defaultLanguage.id;
				} else {
					this.defaultLanguage = new Models.LanguageEntity(attributes.defaultLanguage);
					this.defaultLanguageId = this.defaultLanguage.id;
				}
			}
			if (attributes.defaultRouteId !== undefined) {
				this.defaultRouteId = attributes.defaultRouteId;
			}
			if (attributes.defaultRoute !== undefined) {
				if (attributes.defaultRoute === null) {
					this.defaultRoute = attributes.defaultRoute;
				} else if (attributes.defaultRoute instanceof Models.RouteEntity) {
					this.defaultRoute = attributes.defaultRoute;
					this.defaultRouteId = attributes.defaultRoute.id;
				} else {
					this.defaultRoute = new Models.RouteEntity(attributes.defaultRoute);
					this.defaultRouteId = this.defaultRoute.id;
				}
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
				}
			}
			if (attributes.defaultPricingStrategyProfileId !== undefined) {
				this.defaultPricingStrategyProfileId = attributes.defaultPricingStrategyProfileId;
			}
			if (attributes.defaultPricingStrategyProfile !== undefined) {
				if (attributes.defaultPricingStrategyProfile === null) {
					this.defaultPricingStrategyProfile = attributes.defaultPricingStrategyProfile;
				} else if (attributes.defaultPricingStrategyProfile instanceof Models.PricingStrategyProfileEntity) {
					this.defaultPricingStrategyProfile = attributes.defaultPricingStrategyProfile;
					this.defaultPricingStrategyProfileId = attributes.defaultPricingStrategyProfile.id;
				} else {
					this.defaultPricingStrategyProfile = new Models.PricingStrategyProfileEntity(
						attributes.defaultPricingStrategyProfile,
					);
					this.defaultPricingStrategyProfileId = this.defaultPricingStrategyProfile.id;
				}
			}
			if (attributes.defaultVehicleBaseMeasurementId !== undefined) {
				this.defaultVehicleBaseMeasurementId = attributes.defaultVehicleBaseMeasurementId;
			}
			if (attributes.defaultVehicleBaseMeasurement !== undefined) {
				if (attributes.defaultVehicleBaseMeasurement === null) {
					this.defaultVehicleBaseMeasurement = attributes.defaultVehicleBaseMeasurement;
				} else if (attributes.defaultVehicleBaseMeasurement instanceof Models.MeasurementEntity) {
					this.defaultVehicleBaseMeasurement = attributes.defaultVehicleBaseMeasurement;
					this.defaultVehicleBaseMeasurementId = attributes.defaultVehicleBaseMeasurement.id;
				} else {
					this.defaultVehicleBaseMeasurement = new Models.MeasurementEntity(
						attributes.defaultVehicleBaseMeasurement,
					);
					this.defaultVehicleBaseMeasurementId = this.defaultVehicleBaseMeasurement.id;
				}
			}
		}
	}
}
